import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import './ImageUpload.css';

const ImageUpload = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedContacts = location.state?.selectedContacts || [];
  const addresses = location.state?.addresses || [];
  const allContacts = selectedContacts.length > 0 ? selectedContacts : addresses;
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [htmlPreview, setHtmlPreview] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [salutation, setSalutation] = useState('');
  const [body, setBody] = useState('');
  const [signature, setSignature] = useState('');
  const [backHtml, setBackHtml] = useState('');
  const [convertingText, setConvertingText] = useState(false);
  const [sendingPostcards, setSendingPostcards] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showFailedAddresses, setShowFailedAddresses] = useState(false);
  const [failedAddresses, setFailedAddresses] = useState([]);
  const [successfulCount, setSuccessfulCount] = useState(0);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, []);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setHtmlPreview('');
      setImageUrl('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      // Upload to S3
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('site_id', SITE_CONFIG.SITE_ID);

      const uploadResponse = await fetch('https://tophamtechbe.com/treas-postgrid/s3/upload-image', {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        },
        body: formData
      });

      if (!uploadResponse.ok) throw new Error('Upload failed');
      
      const uploadData = await uploadResponse.json();
      setImageUrl(uploadData.url);

      // Convert to HTML
      const convertResponse = await fetch('https://tophamtechbe.com/treas-postgrid/convert-img-html', {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ image_url: uploadData.url })
      });

      if (!convertResponse.ok) throw new Error('Conversion failed');
      
      const convertData = await convertResponse.json();
      setHtmlPreview(convertData.html);

    } catch (error) {
      console.error('Error:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleTextConvert = async () => {
    if (!salutation && !body && !signature) return;

    setConvertingText(true);
    try {
      const response = await fetch('https://tophamtechbe.com/treas-postgrid/convert-txt-html', {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          salutation: salutation,
          body: body,
          signature: signature
        })
      });

      if (!response.ok) throw new Error('Text conversion failed');
      
      const data = await response.json();
      setBackHtml(data.html);

    } catch (error) {
      console.error('Error:', error);
      alert('Text conversion failed. Please try again.');
    } finally {
      setConvertingText(false);
    }
  };

  const handleSendPostcards = async () => {
    if (!htmlPreview || !backHtml || allContacts.length === 0) {
      alert('Please complete all steps: upload image, create message, and select contacts.');
      return;
    }

    if (!agreedToTerms) {
      alert('Please agree to the terms before sending postcards.');
      return;
    }

    setShowConfirmModal(false);
    setSendingPostcards(true);
    try {
      const response = await fetch('https://tophamtechbe.com/treas-postgrid/pg/send-postcards', {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          site_id: SITE_CONFIG.SITE_ID,
          addresses: allContacts,
          front_html: htmlPreview,
          back_html: backHtml
        })
      });

      if (response.ok) {
        const data = await response.json();
        const failedResults = data.results.filter(result => !result.address_verified);
        
        if (failedResults.length > 0) {
          setFailedAddresses(failedResults.map(result => result.address));
          setSuccessfulCount(data.successful);
          setShowFailedAddresses(true);
        } else {
          navigate('/admin/mail-marketing/success', { 
            state: { contactCount: data.successful } 
          });
        }
      } else {
        throw new Error('Failed to send postcards');
      }

    } catch (error) {
      console.error('Error:', error);
      alert('Failed to send postcards. Please try again.');
    } finally {
      setSendingPostcards(false);
    }
  };



  return (
    <div className="image-upload">
      <div className="upload-header">
        <h1>Step 2: Upload Postcard Image</h1>
        <button onClick={() => navigate('/admin/mail-marketing/contacts')} className="back-button">
          Back to Contact Selection
        </button>
      </div>

      <div className="upload-content">
        <div className="upload-section">
          <div className="upload-card">
            <div className="upload-icon">📤</div>
            <h3>Upload Postcard Image</h3>
            <p>Select a high-quality image for your postcard design</p>
            
            <div className="upload-area">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="file-input"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="upload-label">
                <span className="upload-text">
                  {selectedFile ? '📁 ' + selectedFile.name : '📷 Choose Image File'}
                </span>
              </label>
            </div>
            
            {selectedFile && (
              <div className="action-area">
                <button 
                  onClick={handleUpload} 
                  disabled={uploading}
                  className="upload-button"
                >
                  {uploading ? '⏳ Processing...' : '🚀 Upload & Convert'}
                </button>
              </div>
            )}
            
            <div className="upload-tips">
              <h4>💡 Tips for best results:</h4>
              <ul>
                <li>Use 300 DPI images at 1800 x 1200 pixels for best quality</li>
                <li>Supported formats: JPG, PNG, GIF</li>
                <li><a href="https://www.canva.com/design/DAG75S5xzgA/2HS5ZuFrQlsrgaisl0gvuw/edit?utm_content=DAG75S5xzgA&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton" target="_blank" rel="noopener noreferrer">Try our Canva Template</a></li>
              </ul>
              <div className="file-size-requirement">
                File must be under 500KB
              </div>
            </div>
            
            <div className="text-input-section">
              <h4>✉️ Postcard Message</h4>
              <div className="text-fields">
                <input
                  type="text"
                  placeholder="Salutation (e.g., Dear John)"
                  value={salutation}
                  onChange={(e) => setSalutation(e.target.value)}
                  className="text-input"
                />
                <textarea
                  placeholder="Message body (use Enter for new lines)"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  className="text-area"
                  rows="4"
                />
                <textarea
                  placeholder="Signature (e.g., Best wishes,&#10;Jane&#10;555-123-4567)"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  className="text-area"
                  rows="3"
                />
                
                {(salutation || body || signature) && (
                  <button 
                    onClick={handleTextConvert} 
                    disabled={convertingText}
                    className="convert-button"
                  >
                    {convertingText ? '⏳ Converting...' : '🔄 Preview Message'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="preview-sections">
          {htmlPreview && (
            <div className="preview-section">
              <h2>Postcard Front</h2>
              <div className="preview-container">
                <iframe
                  srcDoc={htmlPreview}
                  className="html-preview"
                  title="Postcard Front Preview"
                />
              </div>
            </div>
          )}
          
          {backHtml && (
            <div className="preview-section">
              <h2>Postcard Back</h2>
              <div className="preview-container">
                <iframe
                  srcDoc={backHtml}
                  className="html-preview"
                  title="Postcard Back Preview"
                />
              </div>
            </div>
          )}
        </div>
        
        {htmlPreview && backHtml && allContacts.length > 0 && (
          <div className="send-section">
            <button 
              onClick={() => setShowConfirmModal(true)}
              disabled={sendingPostcards}
              className="send-button"
            >
              {sendingPostcards ? '📮 Sending Postcards...' : `🚀 Send ${allContacts.length} Postcards`}
            </button>
          </div>
        )}
        
        {showConfirmModal && (
          <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
            <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Confirm Postcard Order</h3>
                <button 
                  className="close-modal"
                  onClick={() => setShowConfirmModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-content">
                <div className="pricing-info">
                  <p><strong>Postcard Cost:</strong> $3.50 per postcard</p>
                  <p><strong>Number of Postcards:</strong> {allContacts.length}</p>
                  <p className="total-cost"><strong>Total Cost: ${(allContacts.length * 3.50).toFixed(2)}</strong></p>
                </div>
                <div className="billing-info">
                  <p>An invoice will be created and charged to your account.</p>
                </div>
                <div className="agreement-section">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                    />
                    I agree to the postcard pricing and billing terms
                  </label>
                </div>
              </div>
              <div className="modal-actions">
                <button 
                  className="cancel-button"
                  onClick={() => setShowConfirmModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="confirm-button"
                  onClick={handleSendPostcards}
                  disabled={!agreedToTerms}
                >
                  Confirm & Send Postcards
                </button>
              </div>
            </div>
          </div>
        )}
        
        {showFailedAddresses && (
          <div className="modal-overlay" onClick={() => setShowFailedAddresses(false)}>
            <div className="failed-addresses-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Address Verification Results</h3>
                <button 
                  className="close-modal"
                  onClick={() => setShowFailedAddresses(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-content">
                <div className="success-info">
                  <p><strong>{successfulCount} postcards were successfully processed.</strong></p>
                </div>
                <div className="failed-info">
                  <p><strong>The following {failedAddresses.length} addresses could not be verified:</strong></p>
                  <div className="failed-addresses-list">
                    {failedAddresses.map((address, index) => (
                      <div key={index} className="failed-address-item">
                        <div className="address-name">{address.Name} - {address.CompanyName}</div>
                        <div className="address-details">
                          {address.Address1} {address.Address2 && `, ${address.Address2}`}<br/>
                          {address.City}, {address.State} {address.Zip}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button 
                  className="confirm-button"
                  onClick={() => {
                    setShowFailedAddresses(false);
                    navigate('/admin/mail-marketing/success', { 
                      state: { contactCount: successfulCount } 
                    });
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUpload;